import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ScrollToTop from "@/components/ScrollToTop";
import BouncingButtons from "@/components/BouncingButtons";
import { useTheme } from "@/hooks/useTheme";

// Pages
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import BrandsPage from "@/pages/BrandsPage";
import ServicesPage from "@/pages/ServicesPage";
import SustainabilityPage from "@/pages/SustainabilityPage";
import ContactPage from "@/pages/ContactPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminContentManager from "@/pages/AdminContentManager";
import EnquiryPage from "@/pages/EnquiryPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  if (!token || !user) {
    toast.error('Please login to access this page');
    return <Navigate to="/" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    toast.error('You do not have permission to access this page');
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const [accentColor, setAccentColor] = useState('green');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    // Load accent color setting
    axios.get(`${API}/pages/settings`).then(r => {
      const color = r.data?.content?.accentColor;
      if (color) {
        setAccentColor(color);
        document.documentElement.setAttribute('data-accent', color);
      }
    }).catch(() => {});
  }, []);

  const handleAccentChange = async (color) => {
    setAccentColor(color);
    document.documentElement.setAttribute('data-accent', color);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.put(`${API}/pages/settings`, { content: { accentColor: color } }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (error) { console.error('Failed to save accent color:', error); }
  };

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <ScrollToTop />
        <BouncingButtons />
        <Routes>
          <Route path="/" element={<HomePage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/products" element={<ProductsPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/products/view" element={<ProductDetailPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/brands" element={<BrandsPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/services" element={<ServicesPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/sustainability" element={<SustainabilityPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/contact" element={<ContactPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route path="/enquiry" element={<EnquiryPage user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />} />
          <Route 
            path="/admin-dashboard" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/content-manager" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminContentManager user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={handleAccentChange} />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors theme={theme} />
    </div>
  );
}

export default App;
